import { Box, BoxProps, Flex, IconButton, Input, Text, useColorModeValue } from '@chakra-ui/react'
import React, { useRef } from 'react'
import { FiEdit2, FiSave, FiX } from 'react-icons/fi'

interface ExampleQuestionCardProps extends BoxProps {
  question: string
  isEditing: boolean
  isEditable: boolean
  onEditStart: () => void
  onEditSave: (newValue: string) => void
  onRemove: () => void
  onClick?: () => void
  onEditCancel: () => void
}

export const ExampleQuestionCard: React.FC<ExampleQuestionCardProps> = ({
  question,
  isEditing,
  isEditable,
  onEditStart,
  onEditSave,
  onRemove,
  onClick,
  onEditCancel,
  ...props
}) => {
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const bgColor = useColorModeValue('white', 'gray.800')
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700')
  const inputRef = useRef<HTMLInputElement>(null)

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onEditSave(inputRef.current?.value || question)
    } else if (e.key === 'Escape') {
      onEditCancel()
    }
  }

  return (
    <Box
      {...props}
      position="relative"
      borderRadius="lg"
      bg={bgColor}
      boxShadow="md"
      border="1px"
      borderColor={borderColor}
      transition="all 0.2s"
      _hover={{
        boxShadow: 'lg',
        transform: 'translateY(-2px)',
        borderColor: 'primaryScheme.200',
      }}
    >
      {isEditing ? (
        <Flex p={0} width="100%" height="100%">
          <Input
            ref={inputRef}
            defaultValue={question}
            onKeyDown={handleKeyDown}
            autoFocus
            size="md"
            borderRadius="lg"
            borderTopRightRadius="0"
            borderBottomRightRadius="0"
            flex="1"
            py={4}
            px={4}
            border='2px solid'
            borderColor='gray.200'
            _hover={{ borderColor: 'gray.300' }}
            _focus={{
              borderColor: 'primaryScheme.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-primaryScheme-500)',
            }}
          />
          <IconButton
            aria-label="Save"
            icon={<FiSave />}
            onClick={() => {
              onEditSave(inputRef.current?.value || question)
            }}
            size="md"
            borderTopLeftRadius="0"
            borderBottomLeftRadius="0"
            colorScheme="primaryScheme"
            height="100%"
          />
        </Flex>
      ) : (
        <Flex
          p={4}
          alignItems="center"
          justifyContent="space-between"
          onDoubleClick={() => isEditable && onEditStart()}
          width="100%"
          height="100%"
        >
          <Text
            flex="1"
            fontSize="md"
            onClick={onClick}
            cursor={onClick ? 'pointer' : 'default'}
          >
            {question}
          </Text>
          {isEditable && (
            <Flex>
              <IconButton
                aria-label="Edit"
                icon={<FiEdit2 />}
                onClick={onEditStart}
                size="sm"
                variant="ghost"
                mr={1}
                _hover={{ bg: hoverBgColor }}
              />
              <IconButton
                aria-label="Remove"
                icon={<FiX />}
                onClick={onRemove}
                size="sm"
                variant="ghost"
                colorScheme="red"
                _hover={{ bg: 'red.50' }}
              />
            </Flex>
          )}
        </Flex>
      )}
    </Box>
  )
}
